"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";
import {
  PLUGIN_CATALOG,
  findPluginById,
  type Plugin,
} from "@/data/plugins";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

type BuySellResult = { ok: boolean; message: string; newBalance?: number };

interface PluginShopProps {
  open: boolean;
  coins: number;
  ownedPluginIds: Record<string, number>;
  inStock: string[];
  onBuy: (pluginId: string) => Promise<BuySellResult>;
  onSell: (pluginId: string) => Promise<BuySellResult>;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Style tokens (cream + ink, 2px borders, no neon, no rounded corners)
// ---------------------------------------------------------------------------

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const DIM = "#8a8377";
const WALNUT = "#5c4a30";
const ALERT = "#c54a4a";
const MOSS = "#7a9a4a";
const GOLD = "#c5a83a";

type Rarity = "common" | "uncommon" | "rare" | "epic" | "legendary";

const RARITY_COLOR: Record<Rarity, string> = {
  common: "#8a8a8a",
  uncommon: "#7a9a4a",
  rare: "#4a7eb8",
  epic: "#a04ab8",
  legendary: "#c5a83a",
};

// ---------------------------------------------------------------------------
// Extended plugin shape (shop optionally reads these fields if present;
// otherwise sensible defaults are derived).
// ---------------------------------------------------------------------------

type ShopPlugin = Plugin & {
  iconKey?: string;
  rarity?: Rarity;
  buyCoins?: number;
  sellCoins?: number;
  description?: string;
  effects?: string[];
};

const DEFAULT_BUY_BY_RARITY: Record<Rarity, number> = {
  common: 100,
  uncommon: 250,
  rare: 600,
  epic: 1500,
  legendary: 4000,
};

function rarityOf(p: ShopPlugin): Rarity {
  if (p.rarity) return p.rarity;
  // Heuristic: plugins that unlock an evolution feel rarer.
  if (p.unlocksEvolution?.minLevel && p.unlocksEvolution.minLevel >= 18) {
    return "epic";
  }
  if (p.unlocksEvolution) return "rare";
  return "uncommon";
}

function buyPriceOf(p: ShopPlugin): number {
  if (typeof p.buyCoins === "number") return p.buyCoins;
  return DEFAULT_BUY_BY_RARITY[rarityOf(p)];
}

function sellPriceOf(p: ShopPlugin): number {
  if (typeof p.sellCoins === "number") return p.sellCoins;
  return Math.floor(buyPriceOf(p) / 2);
}

function iconKeyOf(p: ShopPlugin): string {
  return p.iconKey ?? p.id;
}

function descriptionOf(p: ShopPlugin): string {
  if (p.description) return p.description;
  const parts: string[] = [`Capability category: ${p.category}.`];
  if (p.unlocksEvolution) {
    const { fromSpecies, toSpecies, minLevel } = p.unlocksEvolution;
    parts.push(
      `Unlocks evolution: species ${fromSpecies} \u2192 ${toSpecies}` +
        (minLevel ? ` at Lv ${minLevel}+.` : "."),
    );
  }
  return parts.join(" ");
}

function effectsOf(p: ShopPlugin): string[] {
  if (p.effects && p.effects.length > 0) return p.effects;
  const out: string[] = [`+${p.category} capability`];
  if (p.unlocksEvolution) {
    out.push("Unlocks branching evolution");
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sound helper (no-op safe wrapper)
// ---------------------------------------------------------------------------

type SfxName =
  | "menu_move"
  | "menu_select"
  | "menu_invalid"
  | "item_pickup";

function playSfx(name: SfxName): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    playSfx?: (n: string) => void;
    surggiesSfx?: (n: string) => void;
  };
  try {
    if (typeof w.playSfx === "function") w.playSfx(name);
    else if (typeof w.surggiesSfx === "function") w.surggiesSfx(name);
  } catch {
    // sound is best-effort
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type TabKey = "buy" | "sell";

export function PluginShop({
  open,
  coins,
  ownedPluginIds,
  inStock,
  onBuy,
  onSell,
  onClose,
}: PluginShopProps) {
  const [tab, setTab] = useState<TabKey>("buy");
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState<number>(coins);
  const [toast, setToast] = useState<{ ok: boolean; message: string } | null>(
    null,
  );
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local balance in sync with the prop when it changes from outside.
  useEffect(() => {
    setBalance(coins);
  }, [coins]);

  // Reset cursor when tab changes.
  useEffect(() => {
    setCursor(0);
  }, [tab]);

  // Cleanup any pending toast timer.
  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  // Reset state when closed/reopened.
  useEffect(() => {
    if (!open) {
      setToast(null);
      setBusy(false);
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
        toastTimer.current = null;
      }
    }
  }, [open]);

  // --- Entry lists --------------------------------------------------------

  const buyEntries = useMemo<ShopPlugin[]>(() => {
    const list: ShopPlugin[] = [];
    for (const id of inStock) {
      const p = findPluginById(id) as ShopPlugin | undefined;
      if (p) list.push(p);
    }
    return list;
  }, [inStock]);

  const sellEntries = useMemo<ShopPlugin[]>(() => {
    const list: ShopPlugin[] = [];
    for (const p of PLUGIN_CATALOG as ShopPlugin[]) {
      const count = ownedPluginIds[p.id] ?? 0;
      if (count > 0) list.push(p);
    }
    return list;
  }, [ownedPluginIds]);

  const entries = tab === "buy" ? buyEntries : sellEntries;

  // Clamp cursor when list shrinks.
  useEffect(() => {
    if (cursor > entries.length - 1) {
      setCursor(Math.max(0, entries.length - 1));
    }
  }, [entries.length, cursor]);

  // --- Toast --------------------------------------------------------------

  const showToast = useCallback((msg: { ok: boolean; message: string }) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, 1500);
  }, []);

  // --- Buy / sell actions -------------------------------------------------

  const performBuy = useCallback(
    async (plugin: ShopPlugin) => {
      if (busy) return;
      const price = buyPriceOf(plugin);
      if (balance < price) {
        playSfx("menu_invalid");
        showToast({ ok: false, message: "Not enough coins." });
        return;
      }
      setBusy(true);
      try {
        const result = await onBuy(plugin.id);
        if (result.ok) {
          playSfx("item_pickup");
        } else {
          playSfx("menu_invalid");
        }
        if (typeof result.newBalance === "number") {
          setBalance(result.newBalance);
        }
        showToast({ ok: result.ok, message: result.message });
      } catch (err) {
        playSfx("menu_invalid");
        showToast({
          ok: false,
          message:
            err instanceof Error ? err.message : "Purchase failed.",
        });
      } finally {
        setBusy(false);
      }
    },
    [balance, busy, onBuy, showToast],
  );

  const performSell = useCallback(
    async (plugin: ShopPlugin) => {
      if (busy) return;
      const owned = ownedPluginIds[plugin.id] ?? 0;
      if (owned <= 0) {
        playSfx("menu_invalid");
        showToast({ ok: false, message: "You do not own that plugin." });
        return;
      }
      setBusy(true);
      try {
        playSfx("menu_select");
        const result = await onSell(plugin.id);
        if (!result.ok) {
          playSfx("menu_invalid");
        }
        if (typeof result.newBalance === "number") {
          setBalance(result.newBalance);
        }
        showToast({ ok: result.ok, message: result.message });
      } catch (err) {
        playSfx("menu_invalid");
        showToast({
          ok: false,
          message: err instanceof Error ? err.message : "Sale failed.",
        });
      } finally {
        setBusy(false);
      }
    },
    [busy, onSell, ownedPluginIds, showToast],
  );

  const confirmFocused = useCallback(() => {
    const plugin = entries[cursor];
    if (!plugin) return;
    if (tab === "buy") {
      void performBuy(plugin);
    } else {
      void performSell(plugin);
    }
  }, [cursor, entries, performBuy, performSell, tab]);

  // --- Keyboard -----------------------------------------------------------

  const moveCursor = useCallback(
    (dir: 1 | -1) => {
      if (entries.length === 0) return;
      setCursor((c) => {
        const next = (c + dir + entries.length) % entries.length;
        if (next !== c) playSfx("menu_move");
        return next;
      });
    },
    [entries.length],
  );

  const switchTab = useCallback((dir: 1 | -1) => {
    setTab((t) => {
      const next: TabKey = t === "buy" ? "sell" : "buy";
      // dir is informational — only two tabs.
      void dir;
      if (next !== t) playSfx("menu_move");
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const handled = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Enter",
        "Tab",
        "z",
        "Z",
        "x",
        "X",
        "q",
        "Q",
        "e",
        "E",
        "Escape",
      ];
      if (handled.includes(key)) e.preventDefault();

      switch (key) {
        case "ArrowUp":
          moveCursor(-1);
          break;
        case "ArrowDown":
          moveCursor(1);
          break;
        case "ArrowLeft":
        case "q":
        case "Q":
          switchTab(-1);
          break;
        case "ArrowRight":
        case "e":
        case "E":
          switchTab(1);
          break;
        case "Tab":
          switchTab(e.shiftKey ? -1 : 1);
          break;
        case "Enter":
        case "z":
        case "Z":
          confirmFocused();
          break;
        case "x":
        case "X":
        case "Escape":
          playSfx("menu_move");
          onClose();
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, moveCursor, switchTab, confirmFocused, onClose]);

  if (!open) return null;

  const focused = entries[cursor];
  const focusedOwned = focused ? ownedPluginIds[focused.id] ?? 0 : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Plugin Shop"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        backgroundColor: "rgba(26, 26, 26, 0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        imageRendering: "pixelated",
        padding: "1.25rem",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(68rem, 100%)",
          maxHeight: "100%",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          padding: "0.875rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            borderBottom: `2px solid ${INK}`,
            paddingBottom: "0.5rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="lg"
            className="!text-[#1a1a1a]"
          >
            PLUGIN SHOP
          </PixelText>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              border: `2px solid ${INK}`,
              backgroundColor: CREAM,
              padding: "0.25rem 0.625rem",
            }}
            aria-label={`Balance ${balance} gold`}
          >
            <span
              aria-hidden
              style={{
                width: "0.85rem",
                height: "0.85rem",
                backgroundColor: GOLD,
                border: `2px solid ${INK}`,
                display: "inline-block",
                imageRendering: "pixelated",
              }}
            />
            <span
              className="font-mono font-bold uppercase tracking-widest"
              style={{
                color: INK,
                fontSize: "0.85rem",
                lineHeight: 1,
              }}
            >
              {balance} GOLD
            </span>
          </div>

          <button
            type="button"
            aria-label="Close shop"
            onClick={() => onClose()}
            className="font-mono font-bold uppercase tracking-widest"
            style={{
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              padding: "0.25rem 0.625rem",
              fontSize: "0.85rem",
              lineHeight: 1,
              cursor: "pointer",
              imageRendering: "pixelated",
            }}
          >
            X
          </button>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Shop tabs"
          style={{
            display: "flex",
            border: `2px solid ${INK}`,
          }}
        >
          {(["buy", "sell"] as TabKey[]).map((t) => {
            const active = tab === t;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  if (!active) playSfx("menu_move");
                  setTab(t);
                }}
                className="font-mono font-bold uppercase tracking-widest"
                style={{
                  flex: 1,
                  backgroundColor: active ? INK : CREAM,
                  color: active ? CREAM : INK,
                  border: "none",
                  borderRight:
                    t === "buy" ? `2px solid ${INK}` : "none",
                  padding: "0.5rem 0.75rem",
                  fontSize: "0.9rem",
                  lineHeight: 1,
                  cursor: "pointer",
                  imageRendering: "pixelated",
                  transition: "none",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>

        {/* Body: list + detail */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 18rem",
            gap: "0.75rem",
            border: `2px solid ${INK}`,
            backgroundColor: CREAM,
          }}
        >
          {/* List */}
          <div
            role="listbox"
            aria-label={tab === "buy" ? "Plugins for sale" : "Plugins owned"}
            style={{
              maxHeight: "26rem",
              overflowY: "auto",
              borderRight: `2px solid ${INK}`,
            }}
          >
            {entries.length === 0 ? (
              <div
                className="font-mono uppercase tracking-widest"
                style={{
                  color: DIM,
                  padding: "1rem 0.75rem",
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                }}
              >
                {tab === "buy"
                  ? "Shop is sold out."
                  : "No plugins to sell."}
              </div>
            ) : (
              entries.map((p, i) => {
                const active = i === cursor;
                const owned = ownedPluginIds[p.id] ?? 0;
                const price =
                  tab === "buy" ? buyPriceOf(p) : sellPriceOf(p);
                const disabled =
                  tab === "buy" ? balance < price : owned <= 0;
                const rarity = rarityOf(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    aria-disabled={disabled}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => {
                      setCursor(i);
                      confirmFocused();
                    }}
                    disabled={disabled || busy}
                    className="font-mono font-bold uppercase tracking-widest text-left w-full block relative"
                    style={{
                      backgroundColor: active ? INK : CREAM,
                      color: active ? CREAM : INK,
                      border: "none",
                      borderBottom: `2px solid ${INK}`,
                      padding: "0.5rem 0.75rem 0.5rem 1.5rem",
                      fontSize: "0.82rem",
                      lineHeight: 1,
                      cursor:
                        disabled || busy ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.55 : 1,
                      imageRendering: "pixelated",
                      transition: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.625rem",
                    }}
                  >
                    {active && (
                      <span
                        aria-hidden
                        style={{
                          position: "absolute",
                          left: "0.375rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: CREAM,
                          fontSize: "0.85rem",
                          lineHeight: 1,
                        }}
                      >
                        &#9654;
                      </span>
                    )}

                    {/* Icon */}
                    <span
                      aria-hidden
                      style={{
                        width: "1.75rem",
                        height: "1.75rem",
                        flexShrink: 0,
                        border: `2px solid ${active ? CREAM : INK}`,
                        backgroundColor: active ? WALNUT : "#e6dcc2",
                        imageRendering: "pixelated",
                        backgroundImage: `url(/assets/surggies/plugins/${iconKeyOf(p)}.png)`,
                        backgroundSize: "cover",
                        backgroundPosition: "top left",
                        backgroundRepeat: "no-repeat",
                      }}
                    />

                    {/* Name + rarity */}
                    <span
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.25rem",
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.name}
                      </span>
                      <RarityBadge rarity={rarity} dark={active} />
                    </span>

                    {/* Price */}
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.375rem",
                        fontSize: "0.78rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: "0.7rem",
                          height: "0.7rem",
                          backgroundColor: GOLD,
                          border: `2px solid ${active ? CREAM : INK}`,
                          display: "inline-block",
                        }}
                      />
                      {price} GOLD
                    </span>

                    {/* Owned count */}
                    <span
                      style={{
                        fontSize: "0.7rem",
                        minWidth: "2.5rem",
                        textAlign: "right",
                      }}
                    >
                      x{owned}
                    </span>

                    {/* Action button */}
                    <span
                      aria-hidden
                      style={{
                        backgroundColor: active ? CREAM : INK,
                        color: active ? INK : CREAM,
                        border: `2px solid ${active ? CREAM : INK}`,
                        padding: "0.25rem 0.5rem",
                        fontSize: "0.7rem",
                        lineHeight: 1,
                        marginLeft: "0.25rem",
                      }}
                    >
                      {tab === "buy" ? "BUY" : "SELL"}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail */}
          <div
            style={{
              padding: "0.75rem",
              minHeight: "16rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.625rem",
            }}
            aria-live="polite"
          >
            {focused ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: "2.25rem",
                      height: "2.25rem",
                      border: `2px solid ${INK}`,
                      backgroundColor: "#e6dcc2",
                      imageRendering: "pixelated",
                      backgroundImage: `url(/assets/surggies/plugins/${iconKeyOf(focused)}.png)`,
                      backgroundSize: "cover",
                      backgroundPosition: "top left",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                  <PixelText
                    as="div"
                    glow="none"
                    size="md"
                    className="!text-[#1a1a1a] !tracking-normal !normal-case"
                  >
                    {focused.name}
                  </PixelText>
                </div>

                <RarityBadge rarity={rarityOf(focused)} dark={false} />

                <div
                  className="font-mono"
                  style={{
                    color: INK,
                    fontSize: "0.78rem",
                    lineHeight: 1.4,
                  }}
                >
                  {descriptionOf(focused)}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.25rem",
                    borderTop: `2px solid ${INK}`,
                    paddingTop: "0.5rem",
                  }}
                >
                  <div
                    className="font-mono uppercase tracking-widest"
                    style={{
                      color: WALNUT,
                      fontSize: "0.7rem",
                      lineHeight: 1,
                    }}
                  >
                    EFFECTS
                  </div>
                  <ul
                    className="font-mono"
                    style={{
                      color: INK,
                      fontSize: "0.75rem",
                      lineHeight: 1.4,
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {effectsOf(focused).map((line, i) => (
                      <li key={i}>{`\u2022 ${line}`}</li>
                    ))}
                  </ul>
                </div>

                <div
                  style={{
                    marginTop: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "0.75rem",
                    fontFamily: "var(--font-mono, ui-monospace)",
                    color: INK,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    borderTop: `2px solid ${INK}`,
                    paddingTop: "0.5rem",
                  }}
                >
                  <span>OWNED: {focusedOwned}</span>
                  <span>
                    {tab === "buy"
                      ? `BUY ${buyPriceOf(focused)}`
                      : `SELL ${sellPriceOf(focused)}`}
                  </span>
                </div>
              </>
            ) : (
              <div
                className="font-mono uppercase tracking-widest"
                style={{
                  color: DIM,
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                }}
              >
                Select a plugin.
              </div>
            )}
          </div>
        </div>

        {/* Bottom hint strip */}
        <div
          className="font-mono uppercase tracking-widest"
          style={{
            color: INK,
            fontSize: "0.7rem",
            lineHeight: 1,
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.5rem",
          }}
        >
          Z: {tab === "buy" ? "Buy" : "Sell"} &nbsp;|&nbsp; X: Close
          &nbsp;|&nbsp; {"\u2191\u2193"}: Navigate &nbsp;|&nbsp; Tab/Q/E:
          Switch Tab
        </div>

        {/* Toast */}
        {toast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              left: "50%",
              bottom: "3rem",
              transform: "translateX(-50%)",
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              borderLeft: `6px solid ${toast.ok ? MOSS : ALERT}`,
              padding: "0.5rem 0.875rem",
              fontSize: "0.8rem",
              fontFamily: "var(--font-mono, ui-monospace)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              imageRendering: "pixelated",
              maxWidth: "32rem",
            }}
          >
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rarity badge
// ---------------------------------------------------------------------------

function RarityBadge({ rarity, dark }: { rarity: Rarity; dark: boolean }) {
  const color = RARITY_COLOR[rarity];
  return (
    <span
      className="font-mono font-bold uppercase tracking-widest"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3rem",
        fontSize: "0.65rem",
        color: dark ? CREAM : INK,
        lineHeight: 1,
      }}
    >
      <span
        aria-hidden
        style={{
          width: "0.55rem",
          height: "0.55rem",
          backgroundColor: color,
          border: `2px solid ${dark ? CREAM : INK}`,
          display: "inline-block",
        }}
      />
      {rarity}
    </span>
  );
}

export default PluginShop;
